// LeadershipCard.js
// -----------------------------------------------------------------------------
// Polished, reusable leadership card: square photo, name, role, and optional
// bio. Fully presentational and input-driven: no global reads, no fetching.
// Pulls all visual values from design tokens (radius / shadow / colors).
//
// Props:
//   - image    : string  (default undef)  photo URL (falls back to initials)
//   - name     : string  (required)       person's name
//   - title    : string  (default undef)  role / title (e.g. "Elder")
//   - bio      : node     (default undef)  optional short bio paragraph
//   - imageAlt : string   (default name)   alt text override for the photo
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import AspectRatio from '@mui/joy/AspectRatio';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

const LeadershipCard = ({ image, name, title, bio, imageAlt }) => {
  // Zero-trust: guard the required prop; degrade safely on bad input.
  const safeName = typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;
  if (safeName === null) {
    return null;
  }

  // Constrain optional inputs to safe, renderable values.
  const safeImage = typeof image === 'string' && image.length > 0 ? image : null;
  const safeTitle = typeof title === 'string' && title.length > 0 ? title : null;
  const safeAlt = typeof imageAlt === 'string' && imageAlt.length > 0 ? imageAlt : safeName;

  // Initials fallback when no photo is supplied (local-only derivation).
  const initials = safeName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 'lg',
        boxShadow: 'sm',
        overflow: 'hidden',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 'md' },
      }}
    >
      <CardOverflow>
        <AspectRatio ratio="1">
          {safeImage ? (
            <img src={safeImage} loading="lazy" alt={safeAlt} />
          ) : (
            <Box
              aria-hidden
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.100',
                color: 'primary.600',
              }}
            >
              <Typography level="h2" sx={{ color: 'inherit' }}>
                {initials}
              </Typography>
            </Box>
          )}
        </AspectRatio>
      </CardOverflow>

      <CardContent sx={{ px: 2, py: 2 }}>
        <Typography level="title-lg" sx={{ color: 'text.primary' }}>
          {safeName}
        </Typography>
        {safeTitle ? (
          <Typography level="title" sx={{ color: 'accent.700', mt: 0.25 }}>
            {safeTitle}
          </Typography>
        ) : null}
        {bio ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1.25 }}>
            {bio}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
};

LeadershipCard.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  bio: PropTypes.node,
  imageAlt: PropTypes.string,
};

export default LeadershipCard;
